import type { EngineContext } from "../../context";
import { isCFSFParticipatingState } from "../../constants/cfsfConstants";
import { businessNameControl, personNameControl } from "../../constants/recipientIdentity";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1099MISC } from "./interface/1099-MISC-interface";
import { parseAmount } from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import {
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";

/**
 * Helper function to format SoftwareId to ensure first two digits match tax year
 *
 * According to IRS documentation:
 * - New Software IDs are assigned annually for each tax year
 * - Software IDs must start with the last two digits of the tax year (e.g., "25" for 2025)
 * - The Software ID assigned by IRS for 2025 should already have this format
 *
 * @param softwareId - The Software ID from environment variable
 * @param taxYear - The tax year (e.g., "2025")
 * @returns Formatted Software ID with first two digits matching the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  const yearSuffix = taxYear.slice(-2);

  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10);
  }

  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}

export async function generate1099MISCXml(
  ctx: EngineContext,records: Record1099MISC[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;
  const uniqueId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Check if combined federal state program is enabled for this site
  // Get siteId from the first record (all records in a batch should have the same site)
  const firstRecord = records[0];
  // EXTRACTION NOTE: the CFSF election used to be two database reads
  // (customerFiles -> site id, then siteYear.combinedFederal_stateProgram).
  // The engine has no database; the host answers through ctx.lookups.cfsfEnabled.
  const isCombinedFederalEnabled = await ctx.lookups.cfsfEnabled(
    firstRecord as unknown as Record<string, unknown>
  );

  const form1099MISCDetails = records.map((record) => {
    // Validate and force a valid state code for US addresses
    const validState = (
      record.RECIPIENT_STATE?.substring(0, 2) || "XX"
    ).toUpperCase();

    const mappedState = validState;

    // Format ZIP code to match pattern [0-9]{5}(([0-9]{4})|([0-9]{7}))?
    const formattedZip = record.RECIPIENT_ZIP;

    // Handle name mapping according to IRS schema constraints for 1099-MISC
    // PersonFirstNm: max 20 characters
    // PersonLastNm: required when PersonFirstNm is provided
    const recipientName = record.RECIPIENT_NAME || "";
    const recipientName2 = record.RECIPIENT_NAME2 || "";

    // Robust business entity detection
    const businessIndicators = [
      "LLC",
      "L.L.C.",
      "INC",
      "INCORPORATED",
      "CORP",
      "CORPORATION",
      "LTD",
      "LIMITED",
      "LP",
      "L.P.",
      "CO",
      "COMPANY",
      "COMPANIES",
      "PARTNERSHIP",
      "TRUST",
      "FOUNDATION",
      "ASSOCIATION",
      "SOCIETY",
      "ORGANIZATION",
      "GROUP",
      "HOLDINGS",
      "ENTERPRISES",
      "SOLUTIONS",
    ];

    // Check if either name field contains business indicators
    const nameForBusinessCheck =
      `${recipientName} ${recipientName2}`.toUpperCase();
    const isBusinessEntity = businessIndicators.some((indicator) =>
      nameForBusinessCheck.includes(indicator.toUpperCase())
    );

    // Additional business detection patterns
    const hasBusinessPattern =
      /\b(DBA|D\/B\/A|DOING BUSINESS AS)\b/i.test(nameForBusinessCheck) ||
      /\b(FKA|F\/K\/A|FORMERLY KNOWN AS)\b/i.test(nameForBusinessCheck) ||
      /\b(AKA|A\/K\/A|ALSO KNOWN AS)\b/i.test(nameForBusinessCheck);

    let personName;

    if ((isBusinessEntity || hasBusinessPattern) && recipientName2) {
      // Business entity: Handle schema constraints
      const fullBusinessName = `${recipientName} ${recipientName2}`.trim();

      if (fullBusinessName.length <= 20) {
        // If combined name fits in PersonFirstNm, use minimal PersonLastNm
        personName = {
          PersonFirstNm: fullBusinessName,
          PersonLastNm: "N", // Valid minimal value that matches IRS pattern
        };
      } else if (recipientName.length <= 20) {
        // Use first part in PersonFirstNm, second part in PersonLastNm (truncated if needed)
        personName = {
          PersonFirstNm: recipientName,
          PersonLastNm: recipientName2.substring(0, 20), // PersonLastNm also has 20 char limit
        };
      } else {
        // First name too long, truncate and split
        const truncatedFirstName = recipientName.substring(0, 19); // Leave space for continuation
        const remainder = `${recipientName.substring(
          19
        )} ${recipientName2}`.trim();
        personName = {
          PersonFirstNm: truncatedFirstName,
          PersonLastNm: remainder.substring(0, 20),
        };
      }
    } else {
      // Individual: Follow IRS 1099-MISC full name guidelines with schema compliance
      if (recipientName.length <= 20) {
        // Try to extract a meaningful last name from the full name
        const words = recipientName.trim().split(/\s+/);
        if (words.length >= 2) {
          // Multiple words: use last word as last name, rest as first name
          const lastWord = words[words.length - 1];
          const firstWords = words.slice(0, -1).join(" ");

          if (firstWords.length <= 20 && lastWord.length <= 20) {
            personName = {
              PersonFirstNm: firstWords,
              PersonLastNm: lastWord,
            };
          } else {
            // Names too long, fall back to truncation
            personName = {
              PersonFirstNm: recipientName.substring(0, 20),
              PersonLastNm:
                lastWord.length <= 20 ? lastWord : lastWord.substring(0, 20),
            };
          }
        } else {
          // Single word name - use first letter as last name
          const firstLetter = recipientName.charAt(0).toUpperCase() || "N";
          personName = {
            PersonFirstNm: recipientName,
            PersonLastNm: firstLetter, // Use first letter as minimal last name
          };
        }
      } else {
        // Name too long, need to split intelligently
        const words = recipientName.split(" ");
        let firstName = "";
        let lastName = "";

        // Try to split at word boundaries
        for (let i = 0; i < words.length; i++) {
          const testFirstName = words.slice(0, i + 1).join(" ");
          if (testFirstName.length <= 20) {
            firstName = testFirstName;
            lastName = words.slice(i + 1).join(" ") || "N";
          } else {
            break;
          }
        }

        // Fallback if no good split found
        if (!firstName) {
          firstName = recipientName.substring(0, 20);
          lastName = recipientName.substring(20, 40) || "N";
        }

        personName = {
          PersonFirstNm: firstName,
          PersonLastNm: lastName.substring(0, 20), // Ensure LastNm also fits limit
        };
      }

      // Only add PersonLastNm from RECIPIENT_NAME2 for individuals if it's clearly business-related
      if (
        recipientName2 &&
        (/^(DBA|D\/B\/A|DOING BUSINESS AS)/i.test(recipientName2) ||
          businessIndicators.some((indicator) =>
            recipientName2.toUpperCase().includes(indicator.toUpperCase())
          ))
      ) {
        // Override PersonLastNm with business info if present
        personName.PersonLastNm = recipientName2.substring(0, 20);
      }
    }

    // Build form detail in EXACT schema sequence order per XSD
    const formDetail: any = {
      TaxYr: taxYear,
      RecordId: record.id.toString(),
    };

    // IssuerOfficeCd (optional per schema)
    if ((record as any).ISSUER_OFFICE_CD) {
      formDetail.IssuerOfficeCd = (record as any).ISSUER_OFFICE_CD.substring(
        0,
        4
      );
    }

    // CFSFElectionStateCd (optional, can repeat up to 62 times per schema)
    // Only include if combined federal state program is enabled AND state is in IRS-provided participating list
    // Uses 2-letter state abbreviation (e.g., "OH", "PA", "MN", "RI") per XML schema requirements
    if (
      isCombinedFederalEnabled &&
      validState &&
      validState !== "XX" &&
      isCFSFParticipatingState(validState, taxYear)
    ) {
      // Use the 2-letter state abbreviation (not numeric code)
      formDetail.CFSFElectionStateCd = validState;
    }

    // VoidInd (required, fixed "0" per schema)
    formDetail.VoidInd = "0";

    // CorrectedInd (required per schema)
    formDetail.CorrectedInd = record.corrected ? "1" : "0";

    // PrevSubmittedRecRecipientGrp (optional, for corrections per schema)
    if (record.corrected && (record as any).PREV_UNIQUE_RECORD_ID) {
      formDetail.PrevSubmittedRecRecipientGrp = {
        UniqueRecordId: (record as any).PREV_UNIQUE_RECORD_ID,
        ...((record as any).PREV_RECIPIENT_NAME && {
          RecipientNm: (record as any).PREV_RECIPIENT_NAME,
        }),
        ...((record as any).PREV_RECIPIENT_TIN && {
          RecipientTIN: (record as any).PREV_RECIPIENT_TIN.replace(/-/g, ""),
        }),
      };
    }

    // RecipientDetail (required per schema)
    const miscTin =
      transmitterData.testFileIndicator === "T"
        ? "000000000"
        : (record.RECIPIENT_ID_NUMBER || "").replace(/-/g, "");
    const miscMailingAddressGrp = {
      USAddress: {
        AddressLine1Txt:
          record.RECIPIENT_ADDRESS1?.replace(/\s+/g, " ") ||
          "UNKNOWN_ADDRESS",
        ...(record.RECIPIENT_ADDRESS2 && {
          AddressLine2Txt: record.RECIPIENT_ADDRESS2,
        }),
        CityNm: record.RECIPIENT_CITY || "UNKNOWN_CITY",
        StateAbbreviationCd: mappedState,
        ZIPCd: formattedZip,
      },
    };
    // TY2026 layout V2 carries RECIPIENT_ID_TYPE + split/business names — file
    // businesses as BusinessName + BUSINESS_TIN instead of the legacy
    // business-as-person workaround. Pre-2026 records keep the legacy path.
    // char(1) legacy codes: '1'=EIN, '2'=SSN/ITIN/ATIN, '3'=UNKNOWN
    const idType = (record.RECIPIENT_ID_TYPE || "")
      .toString()
      .trim()
      .toUpperCase();
    const isSplitBusiness =
      idType === "1" ||
      idType === "E" ||
      idType === "EIN" ||
      idType.includes("BUSINESS") ||
      !!record.RECIPIENT_BUSINESS_NAME1?.trim();
    const usesSplitLayout = !!(
      record.RECIPIENT_ID_TYPE ||
      record.RECIPIENT_FIRST_NAME ||
      record.RECIPIENT_BUSINESS_NAME1
    );
    if (usesSplitLayout && isSplitBusiness) {
      const businessName =
        record.RECIPIENT_BUSINESS_NAME1?.trim() ||
        (record.RECIPIENT_NAME || "UNKNOWN").trim();
      formDetail.RecipientDetail = {
        TIN: miscTin,
        TINSubmittedTypeCd: "BUSINESS_TIN",
        BusinessNameControlTxt: businessNameControl(businessName),
        BusinessName: {
          BusinessNameLine1Txt: businessName,
          ...(record.RECIPIENT_BUSINESS_NAME2 && {
            BusinessNameLine2Txt: record.RECIPIENT_BUSINESS_NAME2,
          }),
        },
        MailingAddressGrp: miscMailingAddressGrp,
      };
    } else if (usesSplitLayout) {
      const first = record.RECIPIENT_FIRST_NAME || "UNKNOWN";
      const last = record.RECIPIENT_LAST_NAME || "UNKNOWN";
      formDetail.RecipientDetail = {
        TIN: miscTin,
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        PersonNameControlTxt: personNameControl(record, first, last),
        PersonName: {
          PersonFirstNm: first,
          ...(record.RECIPIENT_MIDDLE_NAME && {
            PersonMiddleNm: record.RECIPIENT_MIDDLE_NAME,
          }),
          PersonLastNm: last,
          ...(record.RECIPIENT_SUFFIX && { SuffixNm: record.RECIPIENT_SUFFIX }),
        },
        MailingAddressGrp: miscMailingAddressGrp,
      };
    } else {
      formDetail.RecipientDetail = {
        TIN: miscTin,
        PersonNameControlTxt: (record.RECIPIENT_NAME || "")
          .trim()
          .substring(0, 4)
          .toUpperCase(),
        PersonName: personName,
        MailingAddressGrp: miscMailingAddressGrp,
      };
    }

    // RecipientAccountNum (optional per schema)
    if (record.ACCOUNT_NUMBER) {
      formDetail.RecipientAccountNum = record.ACCOUNT_NUMBER;
    }

    // SecondTINNoticeInd (required per schema)
    formDetail.SecondTINNoticeInd = "0";

    // FATCAFilingRequirementInd (required per schema)
    formDetail.FATCAFilingRequirementInd = record.FATCA_REQUIREMENT ? "1" : "0";

    // All amount fields (all optional per schema)
    if (record.BOX1_RENTS) {
      formDetail.RentAmt = parseAmount(record.BOX1_RENTS);
    }
    if (record.BOX2_ROYALTIES) {
      formDetail.RoyaltyAmt = parseAmount(record.BOX2_ROYALTIES);
    }
    if (record.BOX3_OTHER_INCOME) {
      formDetail.OtherIncomeAmt = parseAmount(record.BOX3_OTHER_INCOME);
    }
    if (record.BOX4_FED_INCOME_TAX_WITHHELD) {
      formDetail.FederalIncomeTaxWithheldAmt = parseAmount(
        record.BOX4_FED_INCOME_TAX_WITHHELD
      );
    }
    if (record.BOX5_FISHING_BOAT_PROCEEDS) {
      formDetail.FishingBoatProceedsAmt = parseAmount(
        record.BOX5_FISHING_BOAT_PROCEEDS
      );
    }
    if (record.BOX6_MEDICAL_HEALTHCARE_PAYMENTS) {
      formDetail.MedicalHealthCarePaymentsAmt = parseAmount(
        record.BOX6_MEDICAL_HEALTHCARE_PAYMENTS
      );
    }
    // DirectSaleAboveThresholdInd (required per schema)
    formDetail.DirectSaleAboveThresholdInd =
      record.BOX7_DIRECT_SALES_IND === "1" ? "1" : "0";
    if (record.BOX8_SUBSTITUTE_PAYMENTS) {
      formDetail.SubstitutePaymentsAmt = parseAmount(
        record.BOX8_SUBSTITUTE_PAYMENTS
      );
    }
    if (record.BOX9_CROP_INSURANCE_PROCEEDS) {
      formDetail.CropInsuranceProceedsAmt = parseAmount(
        record.BOX9_CROP_INSURANCE_PROCEEDS
      );
    }
    if (record.BOX10_GROSS_PROCEEDS_ATTORNEY) {
      formDetail.AttorneyGrossProceedsPaidAmt = parseAmount(
        record.BOX10_GROSS_PROCEEDS_ATTORNEY
      );
    }
    if (record.BOX11_FISH_PURCHASED_RESALE) {
      formDetail.FishPurchasedForResaleAmt = parseAmount(
        record.BOX11_FISH_PURCHASED_RESALE?.toString()
      );
    }
    if (record.BOX12_SECTION_409A_DEFERRAL) {
      formDetail.Section409ADeferralsAmt = parseAmount(
        record.BOX12_SECTION_409A_DEFERRAL
      );
    }
    // Note: BOX14_GOLDEN_PARACHUTE (ExcessParachutePaymentAmt) is not part of 1099-MISC schema for TY2025
    // TY2026 (schema v5.0.6) Box 13a/13b/14 — XSD order: after Section409ADeferralsAmt,
    // before NonqlfyDeferredCompensationAmt. Emit only for schema year >= 2026 with a
    // value present (the TY2025 XSD forbids these elements).
    if (Number(taxYear) >= 2026 && Number(record.BOX13A_CASH_TIPS) > 0) {
      formDetail.CashTipsAmt = parseAmount(record.BOX13A_CASH_TIPS);
    }
    if (
      Number(taxYear) >= 2026 &&
      (record.BOX13B_TTOC1 || record.BOX13B_TTOC2)
    ) {
      // maxOccurs=2 — array emits one element per code
      formDetail.TreasuryTippedOccupationCd = [
        record.BOX13B_TTOC1,
        record.BOX13B_TTOC2,
      ]
        .filter((c: string) => c && String(c).trim() !== "")
        .map((c: string) => String(c).trim());
    }
    if (
      Number(taxYear) >= 2026 &&
      Number(record.BOX14_OVERTIME_COMPENSATION) > 0
    ) {
      formDetail.OvertimeCompensationAmt = parseAmount(
        record.BOX14_OVERTIME_COMPENSATION
      );
    }
    if (record.BOX15_NONQUALIFIED_COMPENSATION) {
      formDetail.NonqlfyDeferredCompensationAmt = parseAmount(
        record.BOX15_NONQUALIFIED_COMPENSATION
      );
    }
    // StateLocalTaxGrp (optional, can repeat per schema)
    if (shouldIncludeStateLocalTaxGrp(record)) {
      formDetail.StateLocalTaxGrp = getStateLocalTaxGrp(record);
    }

    return formDetail;
  });

  const xmlObj = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear), // NEW for TY2025 - must come before UniqueTransmissionId
        UniqueTransmissionId: uniqueId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: records.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099MISC",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: isCombinedFederalEnabled ? "1" : "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: records.length.toString(),
            IRSubmission1FormTotals: {
              Form1099MISCTotalAmtGrp: {
                FederalIncomeTaxWithheldAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX4_FED_INCOME_TAX_WITHHELD || "0"),
                    0
                  )
                  .toFixed(2),
                // TY2026 (schema v5.0.6): CashTipsAmt sits after FederalIncomeTaxWithheldAmt
                ...(Number(taxYear) >= 2026 &&
                  records.some((r) => Number(r.BOX13A_CASH_TIPS) > 0) && {
                    CashTipsAmt: records
                      .reduce(
                        (sum, r) =>
                          sum + (parseFloat(r.BOX13A_CASH_TIPS || "0") || 0),
                        0
                      )
                      .toFixed(2),
                  }),
                RentAmt: records
                  .reduce((sum, r) => sum + parseFloat(r.BOX1_RENTS || "0"), 0)
                  .toFixed(2),
                RoyaltyAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX2_ROYALTIES || "0"),
                    0
                  )
                  .toFixed(2),
                OtherIncomeAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX3_OTHER_INCOME || "0"),
                    0
                  )
                  .toFixed(2),
                FishingBoatProceedsAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX5_FISHING_BOAT_PROCEEDS || "0"),
                    0
                  )
                  .toFixed(2),
                MedicalHealthCarePaymentsAmt: records
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(r.BOX6_MEDICAL_HEALTHCARE_PAYMENTS || "0"),
                    0
                  )
                  .toFixed(2),
                SubstitutePaymentsAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX8_SUBSTITUTE_PAYMENTS || "0"),
                    0
                  )
                  .toFixed(2),
                CropInsuranceProceedsAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX9_CROP_INSURANCE_PROCEEDS || "0"),
                    0
                  )
                  .toFixed(2),
                AttorneyGrossProceedsPaidAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX10_GROSS_PROCEEDS_ATTORNEY || "0"),
                    0
                  )
                  .toFixed(2),
                FishPurchasedForResaleAmt: records
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(
                        (r.BOX11_FISH_PURCHASED_RESALE || 0).toString()
                      ),
                    0
                  )
                  .toFixed(2),
                Section409ADeferralsAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX12_SECTION_409A_DEFERRAL || "0"),
                    0
                  )
                  .toFixed(2),
                // TY2026 (schema v5.0.6): OvertimeCompensationAmt between Section409A and Nonqlfy
                ...(Number(taxYear) >= 2026 &&
                  records.some(
                    (r) => Number(r.BOX14_OVERTIME_COMPENSATION) > 0
                  ) && {
                    OvertimeCompensationAmt: records
                      .reduce(
                        (sum, r) =>
                          sum +
                          (parseFloat(r.BOX14_OVERTIME_COMPENSATION || "0") ||
                            0),
                        0
                      )
                      .toFixed(2),
                  }),
                NonqlfyDeferredCompensationAmt: records
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(r.BOX15_NONQUALIFIED_COMPENSATION || "0"),
                    0
                  )
                  .toFixed(2),
              },
              // Form1099MISCTotalByStateGrp is REQUIRED when CFSFElectionStateCd is used
              ...(isCombinedFederalEnabled && {
                Form1099MISCTotalByStateGrp: calculateTotalsByState(records, taxYear),
              }),
            },
          },
          IRSubmission1Detail: {
            Form1099MISCDetail: form1099MISCDetails,
          },
        },
      ],
    },
  };

  const xml = builder.build(xmlObj);
  return { xml, taxYear };
}

/**
 * Calculate totals grouped by state for CFSF submissions
 * Returns an array of Form1099MISCTotalByStateGrp objects
 */
function calculateTotalsByState(
  records: Record1099MISC[],
  taxYear: string | number
): Array<{
  StateAbbreviationCd: string;
  TotalReportedRcpntFormCnt: string;
  FederalIncomeTaxWithheldAmt: string;
  StateTaxWithheldAmt: string;
  RentAmt: string;
  RoyaltyAmt: string;
  OtherIncomeAmt: string;
  FishingBoatProceedsAmt: string;
  MedicalHealthCarePaymentsAmt: string;
  SubstitutePaymentsAmt: string;
  CropInsuranceProceedsAmt: string;
  AttorneyGrossProceedsPaidAmt: string;
  FishPurchasedForResaleAmt: string;
  Section409ADeferralsAmt: string;
  NonqlfyDeferredCompensationAmt: string;
}> {
  // Group records by state
  const stateGroups = new Map<string, Record1099MISC[]>();

  records.forEach((record) => {
    const stateCode =
      record.RECIPIENT_STATE?.substring(0, 2).toUpperCase() || "XX";
    if (stateCode !== "XX" && stateCode.length === 2) {
      if (!stateGroups.has(stateCode)) {
        stateGroups.set(stateCode, []);
      }
      stateGroups.get(stateCode)!.push(record);
    }
  });

  // Calculate totals for each state
  const totalsByState: Array<{
    StateAbbreviationCd: string;
    TotalReportedRcpntFormCnt: string;
    FederalIncomeTaxWithheldAmt: string;
    StateTaxWithheldAmt: string;
    RentAmt: string;
    RoyaltyAmt: string;
    OtherIncomeAmt: string;
    FishingBoatProceedsAmt: string;
    MedicalHealthCarePaymentsAmt: string;
    SubstitutePaymentsAmt: string;
    CropInsuranceProceedsAmt: string;
    AttorneyGrossProceedsPaidAmt: string;
    FishPurchasedForResaleAmt: string;
    Section409ADeferralsAmt: string;
    NonqlfyDeferredCompensationAmt: string;
  }> = [];

  stateGroups.forEach((stateRecords, stateCode) => {
    // Calculate state tax withheld from StateLocalTaxGrp data
    const stateTaxWithheld = stateRecords.reduce((sum, r) => {
      const stateTax1 = parseFloat(r.BOX16_STATE_TAX_WITHHELD1 || "0");
      const stateTax2 = parseFloat(r.BOX16_STATE_TAX_WITHHELD2 || "0");
      return sum + stateTax1 + stateTax2;
    }, 0);

    totalsByState.push({
      StateAbbreviationCd: stateCode,
      TotalReportedRcpntFormCnt: stateRecords.length.toString(),
      FederalIncomeTaxWithheldAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX4_FED_INCOME_TAX_WITHHELD || "0"),
          0
        )
        .toFixed(2),
      StateTaxWithheldAmt: stateTaxWithheld.toFixed(2),
      RentAmt: stateRecords
        .reduce((sum, r) => sum + parseFloat(r.BOX1_RENTS || "0"), 0)
        .toFixed(2),
      RoyaltyAmt: stateRecords
        .reduce((sum, r) => sum + parseFloat(r.BOX2_ROYALTIES || "0"), 0)
        .toFixed(2),
      OtherIncomeAmt: stateRecords
        .reduce((sum, r) => sum + parseFloat(r.BOX3_OTHER_INCOME || "0"), 0)
        .toFixed(2),
      FishingBoatProceedsAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX5_FISHING_BOAT_PROCEEDS || "0"),
          0
        )
        .toFixed(2),
      MedicalHealthCarePaymentsAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(r.BOX6_MEDICAL_HEALTHCARE_PAYMENTS || "0"),
          0
        )
        .toFixed(2),
      SubstitutePaymentsAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX8_SUBSTITUTE_PAYMENTS || "0"),
          0
        )
        .toFixed(2),
      CropInsuranceProceedsAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX9_CROP_INSURANCE_PROCEEDS || "0"),
          0
        )
        .toFixed(2),
      AttorneyGrossProceedsPaidAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX10_GROSS_PROCEEDS_ATTORNEY || "0"),
          0
        )
        .toFixed(2),
      FishPurchasedForResaleAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat((r.BOX11_FISH_PURCHASED_RESALE || 0).toString()),
          0
        )
        .toFixed(2),
      Section409ADeferralsAmt: stateRecords
        .reduce(
          (sum, r) => sum + parseFloat(r.BOX12_SECTION_409A_DEFERRAL || "0"),
          0
        )
        .toFixed(2),
      NonqlfyDeferredCompensationAmt: stateRecords
        .reduce(
          (sum, r) =>
            sum + parseFloat(r.BOX15_NONQUALIFIED_COMPENSATION || "0"),
          0
        )
        .toFixed(2),
    });
  });

  return totalsByState;
}

function shouldIncludeStateLocalTaxGrp(record: Record1099MISC): boolean {
  return !!(
    (record.BOX17_PAYER_STATE_NUMBER1 &&
      (parseFloat(record.BOX16_STATE_TAX_WITHHELD1 || "0") > 0 ||
        parseFloat(record.BOX18_STATE_INCOME1 || "0") > 0)) ||
    (record.BOX17_PAYER_STATE_NUMBER2 &&
      (parseFloat(record.BOX16_STATE_TAX_WITHHELD2 || "0") > 0 ||
        parseFloat(record.BOX18_STATE_INCOME2 || "0") > 0))
  );
}

function getStateLocalTaxGrp(record: Record1099MISC): any[] {
  const groups: any[] = [];

  if (
    record.BOX17_PAYER_STATE_NUMBER1 &&
    (parseFloat(record.BOX16_STATE_TAX_WITHHELD1 || "0") > 0 ||
      parseFloat(record.BOX18_STATE_INCOME1 || "0") > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.RECIPIENT_STATE.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: parseAmount(record.BOX16_STATE_TAX_WITHHELD1),
        StateIncomeAmt: parseAmount(record.BOX18_STATE_INCOME1),
      },
    });
  }

  if (
    record.BOX17_PAYER_STATE_NUMBER2 &&
    (parseFloat(record.BOX16_STATE_TAX_WITHHELD2 || "0") > 0 ||
      parseFloat(record.BOX18_STATE_INCOME2 || "0") > 0)
  ) {
    groups.push({
      StateAbbreviationCd: record.BOX17_PAYER_STATE_NUMBER2.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: parseAmount(record.BOX16_STATE_TAX_WITHHELD2),
        StateIncomeAmt: parseAmount(record.BOX18_STATE_INCOME2),
      },
    });
  }

  return groups;
}
